<script>
	/**
	 * [신원] 탭 — 로그인 요구 여부, 받을 개인정보, 카카오 키.
	 * 설계: project-le868/launcher-page-design.md §6-2
	 *
	 * ⚠️ 저장소가 **둘**이다:
	 *    · 공개 설정(loginRequired·고지문구·받을 칸) → BOT_LAUNCHER_SETTINGS.IDENTITY (게시 사이클 O)
	 *    · 카카오 키                                → LAUNCHER_CREDENTIAL (게시 사이클 X)
	 *    키를 저작 설정에 두면 [변경사항 버리기]로 옛 키가 되살아나 로그인이 깨진다.
	 */
	import { createEventDispatcher, onMount } from "svelte";
	import { PRConnect } from "@projectroom/ui-lib";
	import { Section, Input, Textarea, Switch, Button, Label, Select } from "../../lib/ui";
	import { meaningOfProfileKey, tokenListOf, unknownProfileTokens  } from "./launcherSlots.js";

	export let bot;
	/** 서버 IDENTITY 칸. */
	export let identity = {};

	const dispatch = createEventDispatcher();

	$: nIdentity = {
		...(identity || {}),
		loginRequired: identity?.loginRequired ?? false,
		consentText: identity?.consentText ?? "",
		profileNotice: identity?.profileNotice ?? "",
		profileTitle: identity?.profileTitle ?? "",
		profileFields: Array.isArray(identity?.profileFields) ? identity.profileFields : [],
	};

	const emit = (patch) => dispatch("change", { ...nIdentity, ...patch });

	/**
	 * 정보 입력 화면 제목·버튼 문구 칸. 비우면 아래 기본값 — ⚠️ 런타임 ProfileScreen `profileTexts` 와 **같은 규칙**이어야
	 * 한다(회색 글자가 실제로 뜨는 문구를 말해야 하니까).
	 */
	const COPY_ROWS = [
		{ label: "처음 입력할 때", title: "profileEntryTitle", button: "profileEntryButton" },
		{ label: "고칠 때", title: "profileEditTitle", button: "profileEditButton" },
	];
	$: copyDefaults = (() => {
		const name = (nIdentity.profileTitle || "").trim() || "개인정보";
		return {
			profileEntryTitle: `${name} 입력`,
			profileEntryButton: "저장",
			profileEditTitle: `${name} 수정`,
			profileEditButton: `${name} 수정 완료`,
		};
	})();

	/* ── 받을 정보 칸 ───────────────────────────────────────────────────── */
	// ⚠️ 코드에 '생년월일'을 박지 않는다 — 운세는 필요하지만 다른 서비스는 아니다(설계 §0-1).
	//    저작자가 칸을 정의하고, 런타임은 그 정의대로 그린다.
	const FIELD_TYPES = [
		{ value: "text", label: "글자" },
		{ value: "date", label: "날짜" },
		{ value: "time", label: "시간" },
		{ value: "select", label: "선택" },
	];

	/**
	 * 선택지 — 저작은 **쉼표로 한 줄**, 저장은 배열.
	 * ⚠️ 저장되는 값이 곧 이 글자다(표시용 이름이 따로 없다) — 레시피 프롬프트에
	 *    `{profile.키}` 로 그대로 들어가므로 사람이 읽는 말이 그대로 맞다.
	 * ⚠️ 빈 조각과 중복은 버린다 — 빈 버튼이 생기거나 같은 버튼이 둘이 되면 고를 수가 없다.
	 */
	const parseOptions = (v) => [
		...new Set(
			String(v || "")
				.split(",")
				.map((x) => x.trim())
				.filter(Boolean),
		),
	];

	const addField = () =>
		emit({
			profileFields: [...nIdentity.profileFields, { key: "", label: "", type: "text" }],
		});
	const patchField = (i, fields) =>
		emit({
			profileFields: nIdentity.profileFields.map((f, n) => (n === i ? { ...f, ...fields } : f)),
		});
	const removeField = (i) =>
		emit({ profileFields: nIdentity.profileFields.filter((_, n) => n !== i) });

	/**
	 * ⚠️⚠️ 키는 **영문·숫자·밑줄만** 받는다. 안내만 하고 막지 않으면 한글 키가 들어오는데,
	 *    그 값이 그대로 ① 레시피 프롬프트의 `{profile.키}` 토큰 ② DB JSON 키가 된다.
	 *    토큰 치환은 정규식이라 한글도 돌긴 하지만, 저작자가 토큰을 **직접 타이핑**하는 자리라
	 *    오타·조합 차이(NFC/NFD)로 조용히 안 맞는 일이 생긴다.
	 * ⚠️ 지우기만 한다(치환하지 않는다) — 사용자가 친 것 중 **쓸 수 있는 글자만** 남기는 게
	 *    "왜 다른 글자가 나오지"보다 덜 놀랍다. 소문자로 눕혀 대소문자 혼동도 없앤다.
	 */
	const normalizeKey = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9_]/g, "");

	/** 같은 키가 둘이면 뒤엣것이 앞엣것을 덮어쓴다(저장은 되는데 값이 하나만 남는다). */
	$: dupKeys = (() => {
		const seen = new Set();
		const dup = new Set();
		for (const f of nIdentity.profileFields) {
			const k = (f?.key || "").trim();
			if (!k) continue;
			if (seen.has(k)) dup.add(k);
			seen.add(k);
		}
		return dup;
	})();

	/**
	 * 안내 문구에 쓸 수 있는 토큰. ⚠️ 목록을 손으로 적지 않는다 — 아래 '받을 정보'에서 칸 이름을
	 * 바꾸면 안내도 같이 바뀌어야 한다(안 그러면 옛 키를 베껴 쓰게 된다).
	 */
	$: tokenList = tokenListOf(nIdentity.profileFields);
	/** 이 탭의 두 안내 문구에서 선언 안 된 `{profile.X}` 찾기. */
	$: badTokens = unknownProfileTokens(
		[nIdentity.profileNotice, nIdentity.consentText],
		nIdentity.profileFields,
	);

	/* ── 카카오 키 (별도 저장소) ────────────────────────────────────────── */
	// ⚠️ 키 **원문을 화면에 다시 그리지 않는다** — 저작 화면에 다시 띄울 이유가 없고,
	//    띄우는 순간 콘솔 XSS 하나로 새어 나간다. "넣었는지 여부"만 받아 온다.
	/* ── 주소(서브도메인 슬러그) ───────────────────────────────────────── */
	// ⭐ 런처는 자기 서브도메인이 **정규 주소**다 — /view/{botId} 는 여기로 리다이렉트된다(설계 §9-1).
	let slugInput = "";
	let slugSaving = false;
	let slugMessage = "";
	let slugError = "";
	// 저장된 주소. bot.pageUrl 이 곧 `https://{slug}.{base}` 다.
	$: savedUrl = bot.pageUrl || "";
	$: savedSlug = bot.designateUrl || "";

	const saveSlug = () => {
		const next = (slugInput || "").trim().toLowerCase();
		if (!next) return;
		slugSaving = true;
		slugMessage = "";
		slugError = "";
		PRConnect.send("/api/Bot/modifyLauncherSlug", { botId: bot.id, slug: next }, (data) => {
			slugSaving = false;
			if (data.header.status === "success") {
				bot = { ...bot, designateUrl: data.result.slug, pageUrl: data.result.url };
				slugInput = "";
				slugMessage = "주소를 바꿨습니다. 카카오 콘솔의 Redirect URI 도 함께 바꿔주세요.";
			} else {
				slugError = data.header.message || "주소를 바꾸지 못했습니다.";
			}
		});
	};

	let credState = { hasRestKey: false, hasClientSecret: false };
	let restKeyInput = "";
	let clientSecretInput = "";
	let credSaving = false;
	let credMessage = "";

	onMount(() => {
		PRConnect.send("/api/Bot/getLauncherCredentialState", { botId: bot.id }, (data) => {
			if (data.header.status === "success") credState = data.result || credState;
		});
	});

	const saveCredential = () => {
		credSaving = true;
		credMessage = "";
		PRConnect.send(
			"/api/Bot/modifyLauncherCredential",
			{ botId: bot.id, restKey: restKeyInput, clientSecret: clientSecretInput },
			(data) => {
				credSaving = false;
				if (data.header.status === "success") {
					credMessage = "저장했습니다.";
					if (restKeyInput) credState = { ...credState, hasRestKey: true };
					if (clientSecretInput) credState = { ...credState, hasClientSecret: true };
					restKeyInput = "";
					clientSecretInput = "";
				} else {
					credMessage = "저장하지 못했습니다.";
				}
			},
		);
	};

	// 카카오 콘솔에 등록해야 하는 주소 = 런처의 정규 주소(PAGE_URL).
	// ⚠️ 서버가 이 값으로 토큰을 교환하므로 **글자 하나까지 같아야** 한다(다르면 KOE006 인데
	//    메시지만 봐서는 원인을 못 찾는다). 주소를 바꾸면 카카오 쪽도 함께 바꿔야 한다.
	$: redirectUri = savedUrl.split("?")[0];

	let copied = false;
	const copyRedirect = async () => {
		try {
			await navigator.clipboard.writeText(redirectUri);
			copied = true;
			setTimeout(() => (copied = false), 1500);
		} catch (e) {
			copied = false;
		}
	};
</script>

<div class="tw-flex tw-flex-col tw-gap-6">
	<Section title="로그인" description="사용자에게 로그인을 요구할지 정합니다.">
		<div class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
			<Switch
				checked={nIdentity.loginRequired}
				on:click={() => emit({ loginRequired: !nIdentity.loginRequired })}
				aria-label="로그인한 사람만 들어올 수 있게"
			/>
			로그인한 사람만 들어올 수 있게
		</div>
		<p class="tw-mt-2 tw-text-xs tw-text-muted-foreground">
			꺼두면 로그인 없이도 홈을 둘러볼 수 있고, 로그인이 필요한 타일에서만 로그인을 요구합니다.
		</p>
	</Section>

	<Section title="주소" description="런처가 열리는 주소입니다. 런처는 자기 주소를 갖습니다.">
		<div class="tw-flex tw-flex-col tw-gap-3">
			<div class="tw-rounded tw-bg-muted tw-p-3 tw-text-xs">
				<div class="tw-font-medium">현재 주소</div>
				<code class="tw-mt-1 tw-block tw-truncate">{savedUrl || "(아직 없음)"}</code>
			</div>
			<div class="tw-flex tw-items-center tw-gap-3">
				<Label class="tw-w-32 tw-shrink-0 tw-text-sm">주소 바꾸기</Label>
				<Input
					class="tw-min-w-0 tw-flex-1"
					placeholder={savedSlug || "mangsogeum"}
					value={slugInput}
					on:input={(e) => (slugInput = e.target.value)}
				/>
				<Button size="sm" on:click={saveSlug} disabled={slugSaving || !slugInput.trim()}>
					{slugSaving ? "확인 중…" : "변경"}
				</Button>
			</div>
			<p class="tw-text-xs tw-text-muted-foreground">
				영문 소문자·숫자·하이픈만 쓸 수 있습니다(언더스코어 불가). 전체에서 유일해야 합니다.
			</p>
			{#if slugMessage}
				<p class="tw-text-xs tw-text-foreground">{slugMessage}</p>
			{/if}
			{#if slugError}
				<p class="tw-text-xs tw-text-destructive">{slugError}</p>
			{/if}
		</div>
	</Section>

	<Section
		title="카카오 로그인 키"
		description="카카오 개발자 콘솔에서 발급한 키입니다. 저장하면 다시 보이지 않습니다."
	>
		<div class="tw-flex tw-flex-col tw-gap-3">
			<div class="tw-rounded tw-bg-muted tw-p-3 tw-text-xs">
				<div class="tw-font-medium">카카오 콘솔에 등록할 Redirect URI</div>
				<div class="tw-mt-1 tw-flex tw-items-center tw-gap-2">
					<code class="tw-min-w-0 tw-flex-1 tw-truncate">{redirectUri || "(게시 후 생성됩니다)"}</code>
					{#if redirectUri}
						<button type="button" class="tw-shrink-0 tw-underline" on:click={copyRedirect}>
							{copied ? "복사됨" : "복사"}
						</button>
					{/if}
				</div>
				<p class="tw-mt-1 tw-text-muted-foreground">
					이 주소와 **글자 하나까지 같아야** 로그인이 됩니다.
				</p>
			</div>

			<div class="tw-flex tw-items-center tw-gap-3">
				<Label class="tw-w-32 tw-shrink-0 tw-text-sm">REST API 키</Label>
				<Input
					class="tw-flex-1"
					type="password"
					placeholder={credState.hasRestKey ? "입력됨 (바꾸려면 새로 입력)" : "카카오 REST API 키"}
					value={restKeyInput}
					on:input={(e) => (restKeyInput = e.target.value)}
				/>
			</div>
			<div class="tw-flex tw-items-center tw-gap-3">
				<Label class="tw-w-32 tw-shrink-0 tw-text-sm">Client Secret</Label>
				<Input
					class="tw-flex-1"
					type="password"
					placeholder={credState.hasClientSecret ? "입력됨 (바꾸려면 새로 입력)" : "안 켰으면 비워두세요"}
					value={clientSecretInput}
					on:input={(e) => (clientSecretInput = e.target.value)}
				/>
			</div>
			<p class="tw-text-xs tw-text-muted-foreground">
				Client Secret 은 카카오 앱에서 켠 경우에만 넣습니다. 켜두고 비워두면 로그인이 실패합니다.
			</p>
			<div class="tw-flex tw-items-center tw-gap-3">
				<Button
					size="sm"
					on:click={saveCredential}
					disabled={credSaving || (!restKeyInput && !clientSecretInput)}
				>
					{credSaving ? "저장 중…" : "키 저장"}
				</Button>
				{#if credMessage}
					<span class="tw-text-xs tw-text-muted-foreground">{credMessage}</span>
				{/if}
			</div>
		</div>
	</Section>

	<Section
		title="받을 정보"
		description="로그인한 사람에게 추가로 물어볼 항목입니다. 운세처럼 생년월일이 필요하면 여기에 칸을 만드세요."
	>
		<div class="tw-flex tw-flex-col tw-gap-3">
			<!-- ⭐ 정보 입력 흐름 스위치. **없으면 켜짐** — 끄면 false 만 저장한다(켜면 키를 지운다).
			     끄면 서버(launcherGate askProfile)·런타임(App preflight)이 필수 정보로 막지 않고, 설정 메뉴의
			     '{이름} 수정' 줄도 없다. 아래 칸 설정은 그대로 남는다(다시 켜면 돌아온다). -->
			<div class="tw-flex tw-flex-col tw-gap-1">
				<div class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
					<Switch
						checked={identity?.collectProfile !== false}
						on:click={() => emit({ collectProfile: identity?.collectProfile === false ? undefined : false })}
						aria-label="정보 입력 받기"
					/>
					정보 입력 받기
				</div>
				<p class="tw-text-xs tw-text-muted-foreground">
					끄면 정보 입력 안내·입력 화면이 뜨지 않습니다 — [타일]의 '꼭 받아야 하는 정보'가 있어도 묻지 않고,
					설정에 '수정' 줄도 없습니다. 경품 이벤트처럼 개인정보 없이 도는 런처에 쓰세요. 아래 칸 설정은 그대로 남습니다.
				</p>
			</div>
			<!-- ⭐ 비로그인 정보·결과 저장 스위치. **없으면 켜짐** — 끄면 false 만 저장한다(켜면 키를 지운다).
			     끄면 런타임이 받을 정보를 기기에 기억하지 않고(session.ts remember) 서버도 비로그인 결과를 남기지 않는다
			     (Launcher.runRecipe noStore — 이어받기는 서명한 영수증). 로그인 사용자는 무관하다. -->
			<div class="tw-flex tw-flex-col tw-gap-1">
				<div class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
					<Switch
						checked={identity?.storeGuestData !== false}
						on:click={() => emit({ storeGuestData: identity?.storeGuestData === false ? undefined : false })}
						aria-label="비로그인 정보·결과 저장"
					/>
					비로그인 정보·결과 저장
				</div>
				<p class="tw-text-xs tw-text-muted-foreground">
					끄면 로그인하지 않은 사람의 입력 정보를 <strong>기기에 기억하지 않고</strong>(다시 들어오면 다시 입력),
					<strong>서버에도 결과를 남기지 않습니다</strong>. 하루 횟수는 그대로 세고, 같은 날 다시 오면 받은 결과는 다시 볼 수 없는 대신
					<strong>'오늘은 이미 받았어요' 창에 결과 버튼에 이어진 경품 이벤트 버튼</strong>이 뜹니다(정보를 다시 묻지 않습니다).
					'최근 같은 내용 피하기'는 꺼집니다. 결과 이어받기([행운 강화하기] 등)는 그 자리에서 이어서 하면 됩니다.
				</p>
			</div>
			<!-- ⭐ 이 칸들을 사용자 화면에서 **뭐라고 부를지** + 화면 제목·버튼 문구. '개인정보'는 운세에선 딱딱하다
			     ('운세정보'가 맞다). 문구 칸을 비우면 부르는 이름으로 만든다(런타임 ProfileScreen `profileTexts` — 기본값을
			     여기 회색 글자로 똑같이 보여준다. 규칙을 바꾸면 둘 다).
			     ⭐ 들어온 길이 둘이다: 처음 입력(필수 정보가 비어 막혀서 옴) / 고치기(설정 메뉴에서 스스로 옴).
			        시안 처음 입력 = "운세정보 입력" + [행운빵 받으러 가기]. 고치기 제목은 설정 메뉴 줄 이름이기도 하다. -->
			<div class="tw-flex tw-flex-col tw-gap-2">
				<div class="tw-flex tw-items-center tw-gap-2">
					<span class="tw-w-28 tw-shrink-0 tw-text-sm">화면에 부를 이름</span>
					<Input
						class="tw-min-w-0 tw-flex-1"
						placeholder="개인정보"
						maxlength="12"
						value={nIdentity.profileTitle}
						on:input={(e) => emit({ profileTitle: e.target.value })}
					/>
				</div>
				{#each COPY_ROWS as row (row.title)}
					<div class="tw-flex tw-items-center tw-gap-2">
						<span class="tw-w-28 tw-shrink-0 tw-text-sm">{row.label}</span>
						<Input
							class="tw-min-w-0 tw-flex-1"
							placeholder={copyDefaults[row.title]}
							maxlength="20"
							value={nIdentity[row.title] || ""}
							on:input={(e) => emit({ [row.title]: e.target.value })}
						/>
						<Input
							class="tw-min-w-0 tw-flex-1"
							placeholder={copyDefaults[row.button]}
							maxlength="20"
							value={nIdentity[row.button] || ""}
							on:input={(e) => emit({ [row.button]: e.target.value })}
						/>
					</div>
				{/each}
				<p class="tw-pl-[7.5rem] tw-text-xs tw-text-muted-foreground">
					왼쪽 = 화면 제목, 오른쪽 = 버튼. <strong>처음 입력</strong>은 필수 정보가 비어 있어 막혔을 때,
					<strong>고칠 때</strong>는 설정 메뉴에서 열 때 — 고칠 때 제목은 설정 메뉴 줄 이름으로도 쓰입니다. 비우면 회색 글자대로.
				</p>
			</div>
			{#each nIdentity.profileFields as f, i (i)}
				<div class="tw-flex tw-flex-col tw-gap-2">
				<div class="tw-flex tw-items-center tw-gap-2">
					<Input
						class="tw-w-28"
						placeholder="키 (birthdate)"
						value={f.key || ""}
						on:input={(e) => patchField(i, { key: normalizeKey(e.target.value) })}
					/>
					<Input
						class="tw-min-w-0 tw-flex-1"
						placeholder="화면에 보일 이름 (생년월일)"
						value={f.label || ""}
						on:input={(e) => patchField(i, { label: e.target.value })}
					/>
					<Select class="tw-w-24" value={f.type || "text"} on:change={(e) => patchField(i, { type: e.target.value })}>
						{#each FIELD_TYPES as t (t.value)}
							<option value={t.value}>{t.label}</option>
						{/each}
					</Select>
					<button
						type="button"
						class="tw-shrink-0 tw-px-1 tw-text-xs tw-text-destructive"
						on:click={() => removeField(i)}
						aria-label="삭제">✕</button
					>
				</div>

				<!-- ⭐ 선택형일 때만 선택지 줄. 시안의 '양/음력'(양력·음력·음력(윤달))이 이것이다. -->
				{#if f.type === "select"}
					<div class="tw-flex tw-items-center tw-gap-2 tw-pl-[7.5rem]">
						<Input
							class="tw-min-w-0 tw-flex-1"
							placeholder="선택지 (쉼표로 구분) — 양력, 음력, 음력(윤달)"
							value={(f.options || []).join(", ")}
							on:input={(e) => patchField(i, { options: parseOptions(e.target.value) })}
						/>
					</div>
					{#if (f.options || []).length < 2}
						<p class="tw-pl-[7.5rem] tw-text-xs tw-text-destructive">
							⚠️ 선택지가 2개 미만이면 고를 게 없습니다.
						</p>
					{/if}
				{/if}

				<!-- ⭐ 칸 안 안내 글(시안 "닉네임을 입력해 주세요." · "선택해주세요"). 날짜·시간 칸은 비우면 '선택해주세요'. -->
				{#if f.type !== "select"}
					<div class="tw-flex tw-items-center tw-gap-2 tw-pl-[7.5rem]">
						<Input
							class="tw-min-w-0 tw-flex-1"
							placeholder={f.type === "date" || f.type === "time" ? "칸 안 안내 글 — 비우면 '선택해주세요'" : "칸 안 안내 글 (예: 닉네임을 입력해 주세요.)"}
							value={f.placeholder || ""}
							on:input={(e) => patchField(i, { placeholder: e.target.value || undefined })}
						/>
					</div>
				{/if}

				<!-- ⚠️ 날짜·시간은 사용자 기기의 달력·시계로 고르게 돼서 화면엔 "오후 2:30" 처럼 보이지만,
				     저장되는 값(= 레시피 프롬프트의 `{profile.키}`)은 정해진 모양이다. 프롬프트를 쓸 때 헷갈린다. -->
				{#if f.type === "date" || f.type === "time"}
					<p class="tw-pl-[7.5rem] tw-text-xs tw-text-muted-foreground">
						{f.type === "date" ? "저장 값: 1995-03-12 (연-월-일)" : "저장 값: 14:30 (24시간제)"} — 화면에서는 기기의 {f.type === "date" ? "달력" : "시계"}으로 고릅니다.
					</p>
				{/if}

				<!-- ⭐ '모름' 체크박스 — 태어난 시간처럼 모를 수 있는 칸. 고르면 값이 '모름' 글자로 저장된다
				     (필수 정보 게이트는 '답했다'로 보고, 레시피 프롬프트엔 `{profile.키}` 자리에 '모름'이 들어간다).
				     ⚠️ 끄면 키를 지운다(undefined) — false 를 남기면 뜻 없는 흔적이 저작 JSON 에 쌓인다. -->
				<label class="tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-pl-[7.5rem] tw-text-xs">
					<input
						type="checkbox"
						class="tw-mt-0.5"
						checked={f.allowUnknown === true}
						on:change={(e) => patchField(i, { allowUnknown: e.target.checked || undefined })}
					/>
					<span>
						'모름' 체크박스 보이기
						{#if f.allowUnknown}
							<span class="tw-text-muted-foreground">
								— 화면: "{(f.label || "").trim() || f.key || "이 칸"} 모름". 고르면 <code>모름</code> 으로 저장돼 프롬프트에 그대로 들어갑니다.
							</span>
						{/if}
					</span>
				</label>
				</div>
			{/each}
			<div>
				<Button variant="outline" size="sm" on:click={addField}>+ 항목 추가</Button>
			</div>
			<!-- ⚠️ 비어 있거나 겹치는 칸은 **화면에서 티가 나야** 한다 — 저장은 되는데 런타임에서만
			     이상해지는 조합이라(라벨이 비면 영문 키가 그대로 노출되고, 키가 겹치면 값이 하나만
			     남는다) 저작자가 알 길이 없다. -->
			{#if nIdentity.profileFields.some((f) => (f?.key || "").trim() && !(f?.label || "").trim())}
				<p class="tw-text-xs tw-text-destructive">
					⚠️ 이름이 비어 있는 칸이 있습니다 — 사용자 화면에 <strong>영문 키가 그대로</strong> 보입니다.
				</p>
			{/if}
			{#if nIdentity.profileFields.some((f) => (f?.label || "").trim() && !(f?.key || "").trim())}
				<p class="tw-text-xs tw-text-destructive">
					⚠️ 키가 비어 있는 칸이 있습니다 — 그 칸은 저장되지 않습니다.
				</p>
			{/if}
			{#each nIdentity.profileFields.filter((f) => meaningOfProfileKey(f?.key)) as f (f.key)}
				<p class="tw-text-xs tw-text-muted-foreground">
					ℹ️ <code>{f.key}</code> 는 뜻이 정해진 칸입니다 — {meaningOfProfileKey(f.key)}
				</p>
			{/each}
			{#if dupKeys.size > 0}
				<p class="tw-text-xs tw-text-destructive">
					⚠️ 키가 겹칩니다({[...dupKeys].join(", ")}) — 값이 하나만 남습니다.
				</p>
			{/if}
			<p class="tw-text-xs tw-text-muted-foreground">
				<strong>키</strong>는 영문·숫자·밑줄만 됩니다(자동으로 걸러집니다) — 레시피 프롬프트에서
				<code>{"{profile.키}"}</code> 로 씁니다. <strong>이름</strong>은 사용자 화면에 그대로 보이는
				말이라 둘 다 채워야 합니다.
			</p>
		</div>
	</Section>

	<Section
		title="개인정보 화면 안내 칩"
		description="개인정보 입력 화면 맨 위에 체크 표시와 함께 한 줄로 뜹니다. 비우면 안 나옵니다."
	>
		<Input
			placeholder="개인정보 수정은 하루에 1회만 가능해요."
			value={nIdentity.profileNotice || ""}
			on:input={(e) => emit({ profileNotice: e.target.value })}
		/>
	</Section>

	<Section title="개인정보 안내 문구" description="로그인·정보 입력 화면에 그대로 보입니다.">
		<Textarea
			rows="3"
			placeholder="입력하신 개인정보는 운세 결과 제공을 위한 용도로만 사용되며, 관련 법령에 따라 안전하게 관리됩니다."
			value={nIdentity.consentText}
			on:input={(e) => emit({ consentText: e.target.value })}
		/>
		<p class="tw-mt-2 tw-text-xs tw-text-muted-foreground">
			생년월일처럼 개인정보를 받는다면 수집 목적·보관기간을 적어두세요.
		</p>
	</Section>

	<!-- ⭐ 두 안내 문구에 치환 토큰이 돈다(런타임 tokens.ts). 목록은 위 '받을 정보'에서 나온다. -->
	<Section title="안내 문구에 쓸 수 있는 값" description="위 두 문구와 [화면] 탭의 말풍선·안내에 그대로 쓸 수 있습니다.">
		<p class="tw-text-xs tw-text-muted-foreground">
			{#each tokenList as tk (tk)}<code class="tw-mx-0.5">{tk}</code>{/each}
		</p>
		<p class="tw-mt-2 tw-text-xs tw-text-muted-foreground">
			⚠️ 값이 아직 없으면 <code>{"{profile.키}"}</code> 는 <strong>빈칸</strong>이 되고,
			닉네임은 <strong>'너'</strong>로 나옵니다.
			<br />닉네임도 <strong>여기서 만드는 한 칸</strong>입니다 — <code>{"{profile.nickname}"}</code>
			으로 씁니다(소셜 로그인 이름이 미리 채워지고, 랭킹에 그 이름이 보입니다).
		</p>
		{#if badTokens.length > 0}
			<p class="tw-mt-2 tw-text-xs tw-text-destructive">
				⚠️ 위 안내 문구에 없는 칸을 가리키는 값이 있습니다(<!--
				-->{#each badTokens as k, i (k)}<code>{"{profile." + k + "}"}</code>{#if i < badTokens.length - 1}, {/if}{/each})
				— 화면에서 <strong>빈칸으로 사라집니다.</strong>
			</p>
		{/if}
	</Section>
</div>
